import {
    Dialog,
    DialogDescription,
    DialogHeader,
    Grid,
    useIntl,
    useToast,
    InputProps,
    GridRow,
    Input,
    DateTimeInput,
    GridColumn,
    Select,
    Icon,
    SelectOption,
    ToastLevel,
} from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { FC, useCallback, useEffect, useState } from "react"
import { addStockAllocation, editingProcess, getFilteredSiteOptions } from "./stock-allocation-modal-helper"
import { StockAllocationRow } from "features/iot/stock-management/models/stock-management-models"
import { convertDateToDDMMMYYYY, convertDDMMMYYYYtoDate, getTodayDateString } from "components/utils/date-formatter-utils"
import customerStore from "features/organizations/customers/store/customer-store"
import { validateDecimal, validateText } from "validators/inputValidators"
import { convertValueToOption } from "components/common/select-component-helper"
import ModalsButtons from "components/common/modals-buttons"

interface StockAllocateModalProps {
    isOpen: boolean | undefined
    onClose: () => void
    existAbleToCommitSensorsNumber: number
    stockAllocateRow: StockAllocationRow | undefined
    partNumber: string
}

const StockAllocateModal: FC<StockAllocateModalProps> = observer(
    ({ isOpen, onClose, existAbleToCommitSensorsNumber, stockAllocateRow, partNumber }) => {
        const { formatMessage } = useIntl()
        const { setMessage } = useToast()
        const maxQunatity = existAbleToCommitSensorsNumber + (stockAllocateRow?.quantity ?? 0)
        const [errorMessage, setErrorMessage] = useState<string>("")
        const [datePickerError, setDatePickerError] = useState<string>("")
        const [isEditing, setIsEditing] = useState<boolean>(Boolean(stockAllocateRow?.id))
        const [loading, setLoading] = useState<boolean>(false)
        const [isFormModified, setIsFormModified] = useState<boolean>(false)
        const [allocationDate, setAllocationDate] = useState<Date | null>(convertDDMMMYYYYtoDate(stockAllocateRow?.date ?? getTodayDateString()))
        const [pickedCustomer, setPickedCustomer] = useState<SelectOption>()
        const [pickedSite, setPickedSite] = useState<SelectOption>()
        const [quantity, setQuantity] = useState<number>(existAbleToCommitSensorsNumber)
        const [listOfCustomers, setListOfCustomers] = useState<SelectOption[]>([])
        const [listOfSites, setListOfSites] = useState<SelectOption[]>([])

        const { customers, loadAllCustomers } = customerStore

        /**
         * useEffect hook to load all customers when the component mounts or when the customers list is empty.
         * It ensures that the customer data is fetched and available for use in the component.
         */
        useEffect(() => {
            if (!customers.length) {
                loadAllCustomers()
            }
        }, [])

        /**
         * useEffect hook to update the listOfCustomers state whenever the customers data changes.
         * This effect transforms the customers data into a format suitable for the Select component options.
         */
        useEffect(() => {
            setListOfCustomers(
                customers.map(s => {
                    return { text: s.name, value: s.id }
                }),
            )
        }, [customers])

        /**
         * useEffect hook to handle changes in the selected customer (pickedCustomer).
         * Based on the selected customer, it updates the listOfSites with the sites related to that customer.
         * If no customer is selected, it clears the listOfSites.
         */
        useEffect(() => {
            if (!pickedCustomer?.value) {
                setListOfSites([])
                return
            }

            const findCustomer = customers.find(s => s.id === pickedCustomer.value)
            const filteredSiteOptions = getFilteredSiteOptions(findCustomer?.sites)
            setListOfSites(filteredSiteOptions)
            setPickedSite({ text: "Unknown", value: "" })
        }, [pickedCustomer])

        /**
         * useEffect hook to handle the stockAllocateRow data.
         * It sets form fields such as isEditing, quantity, and allocationDate based on the selected stockAllocateRow.
         * If stockAllocateRow is not present, it resets these fields to default values.
         */
        useEffect(() => {
            if (stockAllocateRow) {
                setIsEditing(Boolean(stockAllocateRow.id))
                setQuantity(stockAllocateRow.quantity)

                if (stockAllocateRow.date) {
                    const temp = convertDDMMMYYYYtoDate(stockAllocateRow.date)
                    setAllocationDate(temp)
                }
            } else {
                setIsEditing(false)
            }
        }, [stockAllocateRow, customers])

        /**
         * useEffect hook to update pickedCustomer and pickedSite based on the listOfCustomers and listOfSites.
         * It searches for and sets the pickedCustomer and pickedSite based on the stockAllocateRow's customer and site.
         */
        useEffect(() => {
            if (!pickedCustomer && listOfCustomers.length) {
                const findCustomer = listOfCustomers.find(s => s.text.toLocaleLowerCase() === stockAllocateRow?.customerName?.toLocaleLowerCase())
                setPickedCustomer(findCustomer)
            }
            if (listOfSites.length) {
                const findSite = listOfSites.find(s => s.text.toLocaleLowerCase() === stockAllocateRow?.siteName?.toLocaleLowerCase())
                if (findSite) {
                    setPickedSite(findSite)
                } else {
                    setPickedSite({ text: "Unknown", value: "" })
                }
            }
        }, [listOfCustomers, listOfSites])

        /**
         * Resets the form fields to their initial state.
         * This function is called to clear the form after submission or when the user chooses to reset the form.
         * It sets the editing mode to false, clears the allocation date, resets the selected customer and site to empty values,
         * clears the lists of customers and sites, and sets the quantity back to zero.
         */
        const resetForm = (): void => {
            setIsEditing(false)
            setAllocationDate(null)
            setPickedCustomer({ text: "", value: "" })
            setPickedSite({ text: "", value: "" })
            setListOfCustomers([])
            setListOfSites([])
            setQuantity(0)
        }

        /**
         * Validates the form fields before submission.
         * Checks if the required fields are filled out correctly and sets an error message if validation fails.
         * The function specifically validates that a customer and a site are selected, the quantity is within an acceptable range,
         * and an allocation date is set.
         *
         * @returns {boolean} - Returns true if the form is valid, otherwise false.
         */
        function validateForm(): boolean {
            if (!pickedCustomer?.value || quantity < 1 || quantity > maxQunatity || allocationDate === null) {
                setErrorMessage("Please make sure to fill all required fields.")
                return false
            }
            return true
        }

        /**
         * Handles the form submission event. It prevents the default form submission, manages loading state,
         * validates the form, submits the data for addition or editing based on the editing state, and handles the response.
         *
         * @param {React.FormEvent} e - The event object associated with the form submission.
         */
        const handleSubmit = async (e: React.FormEvent): Promise<void> => {
            e.preventDefault()
            try {
                setLoading(true)

                setErrorMessage("")

                // Validates the form fields and aborts submission if validation fails.
                if (!validateForm()) {
                    return
                }
                // Checks if any modifications have been made to the form data to avoid unnecessary submissions.
                if (!isFormModified) {
                    setErrorMessage("Nothing has been modified!")
                    return
                }

                // Determines whether to call the editing or addition process based on the isEditing state.
                const requestResult = isEditing
                    ? await editingProcess(isEditing, partNumber, stockAllocateRow ?? null, quantity, allocationDate, pickedCustomer, pickedSite)
                    : await addStockAllocation(quantity, partNumber, allocationDate, pickedCustomer, pickedSite)

                // Handles the response from the submission process.
                if (requestResult && !requestResult.passed) {
                    setErrorMessage("Somthing Wrong has been happen, please check the logs")
                    setLoading(false)
                    return
                } else if (requestResult && requestResult.passed) {
                    setMessage(isEditing ? "Stock Allocation has been Updated!" : "Stock Allocation has been added!", ToastLevel.SUCCESS, 3000)
                    console.log("Form submitted successfully!", requestResult.message)
                }

                onCloseProcess()
            } catch (e) {
                setErrorMessage("Somthing Wrong has been happen, please check the logs")
                console.error(e)
            } finally {
                setLoading(false)
            }
        }

        /**
         * Resets the form to its initial state and closes the modal/dialog.
         */
        const onCloseProcess = (): void => {
            resetForm()
            onClose()
        }

        /**
         * Handles changes to the quantity input field. It updates the quantity state and sets the form as modified.
         *
         * @param {string} value - The new value of the quantity input field.
         */
        const handleQunatityChange = useCallback((value: string) => {
            setErrorMessage("")
            const num = parseInt(value, 10)
            if ((num < quantity && num > 0) || num <= maxQunatity) {
                setQuantity(num)
                setIsFormModified(true)
            } else {
                setErrorMessage(`The number must be over 0 and less or qual the number of Sensors: ${maxQunatity}`)
                return
            }
        }, [])

        /**
         * Handles changes to the date input field. It updates the allocationDate state and sets the form as modified.
         *
         * @param {string} value - The new value of the date input field.
         */
        const handleDateChange = useCallback((value: string) => {
            try {
                setDatePickerError("")
                const date = new Date(value)
                if (isNaN(date.getDate())) {
                    setDatePickerError("Invalid Date!")
                } else {
                    setAllocationDate(date)
                    setIsFormModified(true)
                }
            } catch (e) {
                console.log(e)
                const errorMessage = e instanceof Error ? e.message : "An error occurred while setting the date."
                setDatePickerError(errorMessage)
            }
        }, [])

        /**
         * Updates the pickedCustomer state based on the user's selection.
         *
         * @param {SelectOption} pickedValueresult - The selected customer option.
         */
        const onCustomerChange = (pickedValueresult: SelectOption) => {
            setPickedCustomer(pickedValueresult)
            setIsFormModified(true)
        }

        /**
         * Updates the pickedSite state based on the user's selection.
         *
         * @param {SelectOption} pickedValueresult - The selected site option.
         */
        const onSiteChange = (pickedValueresult: SelectOption) => {
            setPickedSite(pickedValueresult)
            setIsFormModified(true)
        }

        /**
         * Configures labels for form fields and buttons using localized messages.
         * This object maps form field identifiers to their display text, supporting internationalization.
         */
        const labels = {
            header: isEditing
                ? `${formatMessage("stockAllocation.editAllocation")}`
                : `${formatMessage("stockAllocation.addAllocation")}: ${partNumber}`,
            description: formatMessage("stockAllocation.description"),
            date: formatMessage("headings.date"),
            customer: formatMessage("headings.customer"),
            site: formatMessage("headings.site"),
            quantity: formatMessage("headings.quantity"),
        }

        /**
         * Defines configuration for input fields in the form, including validation rules and placeholders.
         * This setup helps to maintain consistency in form field definitions and simplifies adjustments.
         */
        const inputConfig = {
            customer: {
                id: "customerSelect",
                label: labels.customer,
                placeholder: `Type ${labels.customer} here...`,
                type: "string",
                isRequired: true,
                validate: validateText,
                listOfCustomer: [],
            },
            site: {
                id: "siteSelect",
                label: labels.site,
                placeholder: `Type ${labels.site} here...`,
                type: "string",
                isRequired: true,
                validate: validateText,
                listOfSites: [],
            },
            quantity: {
                id: "quantity",
                label: labels.quantity,
                placeholder: `Type ${labels.quantity} here...`,
                type: "number",
                isRequired: true,
                validate: validateDecimal,
                maxValue: maxQunatity,
            },
        }

        /**
         * Renders the input fields for quantity and date, as well as customer and site selectors.
         * Utilizes the configuration object to standardize the creation and behavior of these fields.
         *
         * @returns {React.ReactNode} - A collection of React components that represent the form fields.
         */
        const getInputfields = (): React.ReactNode => {
            const inputQuantityProps: InputProps = {
                id: inputConfig.quantity.id,
                label: inputConfig.quantity.label,
                placeholder: inputConfig.quantity.placeholder,
                validate: inputConfig.quantity.validate,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleQunatityChange(e.target.value),
                type: inputConfig.quantity.type,
                spanning: true,
                heightClass: "small",
                required: inputConfig.quantity.isRequired,
                uiRequired: inputConfig.quantity.isRequired,
                value: quantity,
                max: inputConfig.quantity.maxValue,
                min: 1,
            }

            return (
                <>
                    <GridRow key={"quantityDateSelectorsRow"}>
                        <GridColumn className='allocationDate'>
                            <DateTimeInput
                                label={labels.date}
                                onChange={(value: string | any) => handleDateChange(value.toString())}
                                value={convertDateToDDMMMYYYY(allocationDate)}
                                format='DD-MMM-YYYY'
                                timeFormat={false}
                                error={datePickerError}
                            />
                        </GridColumn>
                        <GridColumn>
                            <Input {...inputQuantityProps} />
                        </GridColumn>
                    </GridRow>
                    <GridRow key={"customersSitesSelectorsRow"} style={{ marginBottom: "var(--size-m)" }}>
                        <GridColumn>
                            <Select
                                id={inputConfig.customer.id}
                                icon={<Icon className='icon-search-16' />}
                                label={inputConfig.customer.label}
                                onChange={value => onCustomerChange(convertValueToOption(value, listOfCustomers))}
                                options={listOfCustomers}
                                value={pickedCustomer?.text}
                                required={inputConfig.customer.isRequired}
                            />
                        </GridColumn>
                        <GridColumn>
                            <Select
                                id={inputConfig.site.id}
                                icon={<Icon className='icon-search-16' />}
                                label={inputConfig.site.label}
                                onChange={value => onSiteChange(convertValueToOption(value, listOfSites))}
                                options={listOfSites}
                                value={pickedSite?.text}
                                required={inputConfig.site.isRequired}
                            />
                        </GridColumn>
                    </GridRow>
                </>
            )
        }

        return (
            <Dialog dimmer open={isOpen} size={"small"} style={{ maxWidth: "512px" }}>
                <DialogHeader username={""}>{labels.header}</DialogHeader>
                {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
                <DialogDescription style={{ marginBottom: "var(--size-xxl)" }}>{labels.description}</DialogDescription>
                {customers.length < 1 ? <>{formatMessage("customer.loading")}</> : <Grid columns={2}>{getInputfields()}</Grid>}
                <div style={{ marginBottom: "var(--size-xxl)" }} />
                <ModalsButtons handleSubmit={handleSubmit} onCloseProcess={onCloseProcess} isLoading={loading} />
            </Dialog>
        )
    },
)

export default StockAllocateModal
